import { DropZone, Box, VerticalStack, LegacyStack, Thumbnail, Text, Button, ProgressBar, HorizontalStack, Spinner } from '@shopify/polaris';
import { NoteMinor } from '@shopify/polaris-icons';
import { useState, useCallback } from 'react';

import { uploadContentImages } from '../firebase';
import { uploadToVimeo, getVimeoUploadLink } from '../vimeo';

export default function Uploader({ uploadCallback, contentID, type = "videoFile", dealID }) {
  const [file, setFile] = useState(null);
  const [files, setFiles] = useState([]); // for multiple files
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);

  const handleDropZoneDrop = useCallback(
    (_dropFiles, acceptedFiles, _rejectedFiles) => {
      if (type === 'videoFile') {
        setFile(acceptedFiles[0])
      } else {
        setFiles(acceptedFiles.slice(0, 10))
      }
    },
    [],
  );

  async function uploadVideo() {
    try {
      setLoading(true)
      const { uploadLink, vimeoID } = await getVimeoUploadLink(file, contentID, dealID)
      uploadToVimeo(file, uploadLink, setProgress, () => uploadCallback(vimeoID)) // run async while checking progress
    } catch (e) {
      console.error(e)
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }

  async function uploadImages() {
    try {
      setLoading(true)
      const imageRes = await uploadContentImages(contentID, files)
      uploadCallback(imageRes)
    } catch (e) {
      console.error(e)
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }

  const validImageTypes = ['image/gif', 'image/jpeg', 'image/png'];

  const fileUpload = (!file && !files?.length) && <DropZone.FileUpload actionTitle={type === "videoFile" ? "Add Video" : "Add Images"} actionHint={type === 'videoFile' ? "Max Size 1GB" : "10 Maximum"} />;
  const uploadedFiles = (file || files?.length) ? (
    <Box padding="5">
      <LegacyStack vertical>
        <VerticalStack gap="2" inlineAlign="center">
          {
            file ? (
              <Thumbnail
                size="small"
                alt={file.name}
                source={
                  NoteMinor
                }
              />
            ) : (
              <HorizontalStack gap="2">
                {
                  files?.map((f, i) => (
                    <Thumbnail
                      key={i}
                      size="small"
                      alt={f.name}
                      source={
                        validImageTypes.includes(f.type)
                          ? window.URL.createObjectURL(f)
                          : NoteMinor
                      }
                    />
                  )
                  )
                }
              </HorizontalStack>
            )
          }
          <Text variant="bodySm">
            {file ? file.name : files[0]?.name + (files?.length > 1 ? ` and ${files.length - 1} more` : '')}{' '}
          </Text>
          <Text variant="bodyMd" as="p">
            {file ? (file.size / 1000000).toFixed(1) : files.reduce((a, f) => a + ((f.size || 0) / 1000000), 0).toFixed(1)} MB
          </Text>
        </VerticalStack>
      </LegacyStack>
    </Box>
  ) : null;

  if (progress === 100) {
    return (
      <HorizontalStack gap="2">
        <Spinner accessibilityLabel="Loading" size="large" />
        <VerticalStack gap="1">
          <Text variant="headingMd">Finishing Upload...</Text>
          <Text>Wait just a moment</Text>
        </VerticalStack>
      </HorizontalStack>
    )
  }

  return (
    <VerticalStack gap="2">
      <Text>
        {error}
      </Text>
      <DropZone
        accept={type === 'videoFile' ? "video/mp4,video/x-m4v,video/*" : "image/*"}
        onDrop={handleDropZoneDrop}>
        {uploadedFiles}
        {fileUpload}
      </DropZone>
      {
        progress > 0 && progress < 100 ? (
          <VerticalStack gap="2">
            <Text>Uploading... {progress.toFixed(0)}%</Text>
            <ProgressBar progress={progress} size="small" />
          </VerticalStack>
        ) : <Button disabled={!file && !files?.length} loading={loading} onClick={type === 'videoFile' ? uploadVideo : uploadImages}>Upload</Button>
      }
    </VerticalStack>
  );
}
